import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as Localization from 'expo-localization';
import AsyncStorage from '@react-native-async-storage/async-storage';

import enTranslation from './locales/en/translation.json';
import frTranslation from './locales/fr/translation.json';
import { languageFromCurrentUrl } from './urlLanguage';

const STORAGE_KEY = 'user_language';

const languageDetector = {
  type: 'languageDetector' as const,
  async: true,
  detect: async (callback: (lng: string) => void) => {
    try {
      // A ?lang= on the URL wins over everything (see urlLanguage.ts)
      const urlLanguage = languageFromCurrentUrl();
      if (urlLanguage) {
        callback(urlLanguage);
        return;
      }

      // Then the stored user preference
      const storedLanguage = await AsyncStorage.getItem(STORAGE_KEY);
      if (storedLanguage) {
        callback(storedLanguage);
        return;
      }

      // Fall back to device locale
      const deviceLocale = Localization.getLocales()[0];
      const languageCode = deviceLocale?.languageCode || 'en';

      // Map device locale to supported languages
      const supportedLanguage = languageCode.startsWith('fr') ? 'fr' : 'en';
      callback(supportedLanguage);
    } catch (error) {
      console.warn('Language detection failed, defaulting to English:', error);
      callback('en');
    }
  },
  init: () => {},
  cacheUserLanguage: async (lng: string) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, lng);
    } catch (error) {
      console.warn('Failed to cache user language:', error);
    }
  },
};

const resources = {
  en: {
    translation: enTranslation,
  },
  fr: {
    translation: frTranslation,
  },
};

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false, // missingKey logs on every render flood the console; UI uses inline defaults

    interpolation: {
      escapeValue: false, // React already does escaping
    },

    compatibilityJSON: 'v4',

    react: {
      useSuspense: false, // Disable suspense for React Native
    },
  } as any);

export default i18n;

// Export helper functions
export const changeLanguage = async (language: 'en' | 'fr') => {
  await i18n.changeLanguage(language);
};

export const getCurrentLanguage = (): 'en' | 'fr' => {
  return i18n.language as 'en' | 'fr';
};

export const getSupportedLanguages = () => {
  return ['en', 'fr'] as const;
};